import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { GetActionUpdateByIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetActionUpdateByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC action update data to match the GraphQL query structure
 */
function mapTrpcActionUpdateToGraphQL(
  trpcData: GetActionUpdateByIdResponseRow[]
): GetActionUpdateByIdQuery {
  return {
    action_update: trpcData,
  };
}

export const useGetActionUpdateByIdTRPC = (id: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.action.updates.getActionUpdateById.queryOptions({
      id,
    }),
    enabled: trpcEnabled && !skip && !!id,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcError, addNotification, trpcEnabled]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcActionUpdateToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
